export const NUM_MILLISECONDS_PER_SECOND = 1000;
export const NUM_SECONDS_PER_MINUTE = 60;
export const NUM_SECONDS_PER_HOUR = NUM_SECONDS_PER_MINUTE * 60;
export const NUM_SECONDS_PER_DAY = NUM_SECONDS_PER_HOUR * 24;
const NUM_SECONDS_PER_WEEK = NUM_SECONDS_PER_DAY * 7;
const NUM_SECONDS_PER_MONTH = NUM_SECONDS_PER_DAY * 30;
const NUM_SECONDS_PER_YEAR = NUM_SECONDS_PER_DAY * 365;
const SUNDAY_DAY_OF_WEEK = 7;
const MAX_MONTH_DISPLAY_LENGTH = 3;

export const DAYS_IN_WEEK = 7;

export interface TimeIntervals {
  intervalInYears: number;
  intervalInMonths: number;
  intervalInWeeks: number;
  intervalInDays: number;
  intervalInHours: number;
  intervalInMinutes: number;
}
export interface TimeDifferenceFromNow extends TimeIntervals {
  differenceInSeconds: number;
}

export const getReadableMonths = (): string[] => {
  return [
    window.i18next.t('pmwjs_calendar_january'),
    window.i18next.t('pmwjs_calendar_february'),
    window.i18next.t('pmwjs_calendar_march'),
    window.i18next.t('pmwjs_calendar_april'),
    window.i18next.t('pmwjs_calendar_may'),
    window.i18next.t('pmwjs_calendar_june'),
    window.i18next.t('pmwjs_calendar_july'),
    window.i18next.t('pmwjs_calendar_august'),
    window.i18next.t('pmwjs_calendar_september'),
    window.i18next.t('pmwjs_calendar_october'),
    window.i18next.t('pmwjs_calendar_november'),
    window.i18next.t('pmwjs_calendar_december'),
  ];
};

export const getReadableWeekdays = (): string[] => {
  return [
    window.i18next.t('pmwjs_calendar_sunday'),
    window.i18next.t('pmwjs_calendar_monday'),
    window.i18next.t('pmwjs_calendar_tuesday'),
    window.i18next.t('pmwjs_calendar_wednesday'),
    window.i18next.t('pmwjs_calendar_thursday'),
    window.i18next.t('pmwjs_calendar_friday'),
    window.i18next.t('pmwjs_calendar_saturday'),
  ];
};

export const getReadableShortWeekdays = (): string[] => {
  return [
    window.i18next.t('pmwjs_calendar_sun'),
    window.i18next.t('pmwjs_calendar_mon'),
    window.i18next.t('pmwjs_calendar_tue'),
    window.i18next.t('pmwjs_calendar_wed'),
    window.i18next.t('pmwjs_calendar_thu'),
    window.i18next.t('pmwjs_calendar_fri'),
    window.i18next.t('pmwjs_calendar_sat'),
  ];
};

export const getReadableTimePeriodLabels = (): {am: string; pm: string} => {
  return {
    am: window.i18next.t('pmwjs_am'),
    pm: window.i18next.t('pmwjs_pm'),
  };
};

/**
 * Converts time in int seconds to [hh:]mm:ss
 */
export const numberToMMSSFormat = (duration: number): string => {
  const durationInSeconds = duration;
  const hourPart = Math.floor(durationInSeconds / 3600);
  const minutePart = Math.floor((durationInSeconds - hourPart * 3600) / 60);
  const secondPart = Math.floor(durationInSeconds - hourPart * 3600 - minutePart * 60);
  let durationString = '';

  if (hourPart) {
    durationString += `${`00${hourPart.toString(10)}`.slice(-2)}:`;
  }
  durationString += `${`00${minutePart.toString(10)}`.slice(-2)}:${`00${secondPart.toString(10)}`.slice(-2)}`;

  return durationString;
};

export const formatSecondsToTimeString = (seconds: number, options?: {showDecimals?: boolean}): string => {
  const {showDecimals = false} = options || {};

  if (showDecimals) {
    if (seconds < 60) {
      return `${seconds.toFixed(1)}s`;
    }
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(1);
    return `${mins}:${secs.padStart(4, '0')}`;
  }

  if (seconds < 60) {
    return `${Math.floor(seconds)}s`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Formats a duration as whole seconds ("63s", never "1:03"), zero-padded to the digit count maxSeconds
 * needs ("07s" for max 42s), so a ticking label keeps a constant character count. Pair with tabular-nums
 * for a fully stable width.
 */
export const formatSecondsToConstantWidthTimeString = (seconds: number, maxSeconds: number): string => {
  const secondsDigits = Math.floor(maxSeconds).toString().length;
  return `${Math.floor(seconds).toString().padStart(secondsDigits, '0')}s`;
};

export const computeClientTimezoneOffset = (): number => {
  const timezoneOffsetMinutes = new Date().getTimezoneOffset();
  return timezoneOffsetMinutes * 60;
};

export const getDayMonthYearFormat = (date: Date): string => {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();

  return `${year}/${month}/${day}`;
};

export const getMonthDayYearFormat = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${month} / ${day} / ${year}`;
};

export const formatDateFromTimestamp = (timestamp: number): string => {
  const date = getDateFromUnixTimestamp(timestamp);

  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear() % 100;

  const formattedDay = String(day).padStart(2, '0');
  const formattedMonth = String(month).padStart(2, '0');
  const formattedYear = String(year).padStart(2, '0');

  return `${formattedDay}/${formattedMonth}/${formattedYear}`;
};

/**
 * Example: July 23, Wednesday
 * @param date
 */
export const getMonthDateDayFormat = (date: Date): string => {
  const monthName = getReadableMonthNameForDate(date);
  const dateNumber = date.getDate();
  const day = getReadableWeekdayForDate(date);

  return `${monthName} ${dateNumber}, ${day}`;
};

// example: May 23
export const getMonthDateFormat = (date: Date): string => {
  const monthName = getReadableShortMonth(date);
  const dateNumber = date.getDate();

  return `${monthName} ${dateNumber}`;
};

// example 8 sep
export const getShortDateMonthFormat = (date: Date): string => {
  const monthName = getReadableShortMonth(date);
  const dateNumber = date.getDate();

  return `${dateNumber} ${monthName}`;
};

export const getUnixTimestamp = (date: Date): number => {
  return Math.floor(date.getTime() / 1000);
};

export function formatLocalDateOnly(timestamp: number): string {
  const date = getDateFromUnixTimestamp(timestamp);
  const localTime = date.getTime();

  const options = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  } as const;

  return new Intl.DateTimeFormat('en-US', options).format(localTime);
}

export function formatLocalDate(timestamp: number): string {
  const date = getDateFromUnixTimestamp(timestamp);
  const localTime = date.getTime();

  const options = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  } as const;

  return new Intl.DateTimeFormat('en-US', options).format(localTime);
}

/**
 * Pass `timeZone` when the date is a UTC-anchored calendar date (built with `Date.UTC`, so its instant is midnight
 * UTC) rather than a moment in time. Without it the viewer's zone decides the calendar day, and every negative-offset
 * zone — all of the US — renders the day before. Omit it for real instants, which should read in the viewer's zone.
 */
export function formatShortUSDate(date: Date | string | number, timeZone?: Intl.DateTimeFormatOptions['timeZone']): string {
  const options: Intl.DateTimeFormatOptions = {month: 'short', day: 'numeric', year: 'numeric', timeZone};
  return new Date(date).toLocaleDateString('en-US', options);
}

export function formatShortDateWithMonthAndDayOnly(date: Date | string | number): string {
  const options: Intl.DateTimeFormatOptions = {day: 'numeric', month: 'short'};
  return new Date(date).toLocaleDateString('en-US', options);
}

export function formatDateYYYYMMDD(date: Date | string | number): string {
  const options: Intl.DateTimeFormatOptions = {year: 'numeric', month: '2-digit', day: '2-digit'};
  return new Date(date).toLocaleDateString('en-US', options).replace(/\//g, ''); // Remove slashes
}

export const getMonthYearFormatForUTCDate = (date: Date = new Date()): string => {
  const monthNames = getReadableMonths();
  return `${monthNames[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
};

export const getMonthYearFormatForDate = (date: Date = new Date()): string => {
  const monthNames = getReadableMonths();
  return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
};

/**
 * Day, Date Month like "Tuesday, August 23" or "Tue, Aug 23"
 * @param date
 * @param isShort
 */
export const getReadableDayMonthDateFormatForDate = (date: Date = new Date(), isShort = false): string => {
  const month = getReadableMonthNameForDate(date, isShort);
  const weekday = getReadableWeekdayForDate(date, isShort);
  const dateNum = date.toLocaleDateString('en-US', {day: '2-digit'});
  return `${weekday}, ${month} ${dateNum}`;
};

export const getReadableTimeForDate = (date: Date = new Date()): string => {
  const hours: number = date.getHours();
  const minutes: number = date.getMinutes();
  const amPmLabels: {am: string; pm: string} = getReadableTimePeriodLabels();

  const formattedHours = hours % 12 === 0 ? 12 : hours % 12;
  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
  const amPm = hours >= 12 ? amPmLabels.pm : amPmLabels.am;

  return `${formattedHours}:${formattedMinutes}${amPm.toLowerCase().replace(' ', '')}`;
};

export const getDateFromUnixTimestamp = (timestamp: number): Date => {
  return new Date(timestamp * 1000);
};

export const getLocalDate = (date: Date): Date => {
  if (typeof window.PMW.convertUTCDateToLocalDate === 'undefined') {
    const SECONDS_IN_MINUTE = 60;
    const MILLISECONDS_IN_SECOND = 1000;
    return new Date(date.getTime() - date.getTimezoneOffset() * SECONDS_IN_MINUTE * MILLISECONDS_IN_SECOND);
  }

  return window.PMW.convertUTCDateToLocalDate(date) as Date;
};

export const getDatesBetween = (startDate: Date, endDate: Date): Date[] => {
  const dates = [];
  const currentDate = startDate;
  while (currentDate < endDate) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);

    if (currentDate.getDate() === endDate.getDate() && currentDate.getMonth() === endDate.getMonth()) {
      break;
    }
  }

  dates.push(endDate);
  return dates;
};

export const getNextMonth = (now = new Date()): Date => {
  let next;

  if (now.getMonth() === 11) {
    next = new Date(now.getFullYear() + 1, 0, 1);
  } else {
    next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  }

  return next;
};

export const getFirstDayOfMonth = (date = new Date()): Date => {
  const y = date.getFullYear();
  const m = date.getMonth();
  return new Date(y, m, 1);
};

export const getMonthlyCalendarStartDate = (date = new Date()): Date => {
  return getPreviousSunday(getFirstDayOfMonth(date));
};

export const getMonthlyCalendarEndDate = (rows: number, date = new Date()): Date => {
  const endDate = getMonthlyCalendarStartDate(date);
  endDate.setDate(endDate.getDate() + rows * DAYS_IN_WEEK - 1);
  endDate.setHours(23, 59, 59, 0);
  return endDate;
};

export const getPreviousSunday = (date = new Date()): Date => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() - (newDate.getDay() || SUNDAY_DAY_OF_WEEK));
  return newDate;
};

export const getReadableShortMonthForIndex = (monthIndex: number): string => {
  const months = getReadableMonths();
  return months[monthIndex]?.substring(0, MAX_MONTH_DISPLAY_LENGTH) ?? '';
};

export const getReadableMonthForIndex = (monthIndex: number, isShort = false): string => {
  return isShort ? getReadableShortMonthForIndex(monthIndex) : getReadableMonths()[monthIndex];
};

export const getReadableMonthNameForDate = (date: Date, isShort = false): string => {
  return getReadableMonthForIndex(date.getMonth(), isShort);
};

export const getReadableShortMonth = (date: Date): string => {
  return getReadableShortMonthForIndex(date.getMonth());
};

export const getReadableWeekdayForDate = (date: Date, isShort = false): string => {
  return isShort ? getReadableShortWeekdays()[date.getDay()] : getReadableWeekdays()[date.getDay()];
};

export const isToday = (date: Date): boolean => {
  const today = new Date();

  return today.toDateString() === date.toDateString();
};

export const isUpcomingDate = (date: Date): boolean => {
  const today = new Date();
  const toCompare = new Date(date);

  today.setHours(0, 0, 0, 0);
  toCompare.setHours(0, 0, 0, 0);

  return toCompare.getTime() >= today.getTime();
};

export const isCurrentMonth = (date: Date): boolean => {
  const today = new Date();

  return today.getMonth() === date.getMonth();
};

export const isFirstOfMonth = (date: Date): boolean => {
  return date.getDate() === 1;
};

export const getDateAndMonthStringWithOrdinal = (date: Date): string => {
  const nth = (d: number): string => {
    if (d > 3 && d < 21) return 'th';
    switch (d % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  return `${date.getDate().toString() + nth(date.getDate())} ${getReadableMonths()[date.getMonth()]}`;
};

export const getDateStringWithOrdinal = (date: Date): string => {
  const nth = (d: number): string => {
    if (d > 3 && d < 21) return 'th';
    switch (d % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  return date.getDate().toString() + nth(date.getDate());
};

export const getPreviousMonth = (now = new Date()): Date => {
  let prev;
  if (now.getMonth() === 0) {
    prev = new Date(now.getFullYear() - 1, 11, 1);
  } else {
    prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  }

  return prev;
};

export const getCurrentUTCMonthIndex = (): number => {
  return new Date().getUTCMonth();
};

export const getPreviousUTCMonthIndex = (): number => {
  return getPreviousMonthIndex(getCurrentUTCMonthIndex());
};

export const getNextUTCMonthIndex = (): number => {
  return getNextMonthIndex(getCurrentUTCMonthIndex());
};

export const getPreviousMonthIndex = (currentMonthIndex: number): number => {
  return currentMonthIndex === 0 ? 11 : currentMonthIndex - 1;
};

export const getNextMonthIndex = (currentMonthIndex: number): number => {
  return currentMonthIndex === 11 ? 0 : currentMonthIndex + 1;
};

export const getShortMonthNameForCurrentUTCMonth = (): string => {
  const currentMonthIndex = new Date().getUTCMonth();
  return getReadableShortMonthForIndex(currentMonthIndex);
};

export const getShortMonthNameForPreviousUTCMonth = (): string => {
  const currentMonthIndex = new Date().getUTCMonth();
  return getReadableShortMonthForIndex(getPreviousMonthIndex(currentMonthIndex));
};

export const getReadableMonthNameForIndex = (monthIndex: number): string => {
  return getReadableMonths()[monthIndex];
};

export const getPreviousUTCMonthName = (): string => {
  return getReadableMonthNameForIndex(getPreviousUTCMonthIndex());
};
export const getCurrentUTCMonthName = (): string => {
  return getReadableMonthNameForIndex(getCurrentUTCMonthIndex());
};
export const getNextUTCMonthName = (): string => {
  return getReadableMonthNameForIndex(getNextUTCMonthIndex());
};

export const isPastDateByAtleastOneHour = (date: Date): boolean => {
  return new Date().getTime() - date.getTime() >= NUM_SECONDS_PER_HOUR * NUM_MILLISECONDS_PER_SECOND;
};

export const isInThirtyMinutesOrLessFromNow = (date: Date): boolean => {
  const timeDifference = date.getTime() - new Date().getTime();
  return timeDifference >= 0 && timeDifference <= 30 * NUM_SECONDS_PER_MINUTE * NUM_MILLISECONDS_PER_SECOND;
};

export const hasDatedPassed = (date: Date): boolean => {
  return date.getTime() <= new Date().getTime();
};

/**
 * Assumes given date is in UTC time
 *
 * @param date
 */
export const hasDatePassed2 = (date: Date): boolean => {
  const currDate = new Date();

  // compare year
  if (date.getFullYear() < currDate.getUTCFullYear()) {
    return true;
  }
  if (date.getFullYear() > currDate.getUTCFullYear()) {
    return false;
  }

  // compare month
  if (date.getMonth() < currDate.getUTCMonth()) {
    return true;
  }
  if (date.getMonth() > currDate.getUTCMonth()) {
    return false;
  }

  // compare date
  if (date.getDate() < currDate.getUTCDate()) {
    return true;
  }
  if (date.getDate() > currDate.getUTCDate()) {
    return false;
  }
  return hasTimePassed(date);
};

/**
 * Assumes given date is in UTC time
 *
 * @param date
 */
export const hasTimePassed = (date: Date): boolean => {
  const currDate = new Date();

  // compare hours
  if (date.getHours() < currDate.getUTCHours()) {
    return true;
  }
  if (date.getHours() > currDate.getUTCHours()) {
    return false;
  }

  // compare minutes
  if (date.getMinutes() < currDate.getUTCMinutes()) {
    return true;
  }
  if (date.getMinutes() > currDate.getUTCMinutes()) {
    return false;
  }

  // compare seconds
  if (date.getSeconds() < currDate.getUTCSeconds()) {
    return true;
  }
  if (date.getSeconds() > currDate.getUTCSeconds()) {
    return false;
  }

  // compare ms
  if (date.getMilliseconds() < currDate.getUTCMilliseconds()) {
    return true;
  }
  if (date.getMilliseconds() > currDate.getUTCMilliseconds()) {
    return false;
  }
  return false;
};

export const isFutureDate = (date: Date): boolean => {
  return date.getTime() > new Date().getTime();
};

export const isBetweenDates = (dateToCheck: Date, startDate: Date, endDate: Date): boolean => {
  return dateToCheck.getTime() >= startDate.getTime() && dateToCheck.getTime() < endDate.getTime();
};

export const areDatesEqual = (date1: Date, date2: Date): boolean => {
  return date1.toDateString() === date2.toDateString();
};

export const isDateInList = (dateToCheck: Date, dates: Date[]): boolean => {
  return dates.some((date) => {
    return areDatesEqual(date, dateToCheck);
  });
};

export const getNearestQuarterHourFromGivenDate = (startDate: Date): Date => {
  const minutes = startDate.getMinutes() + 1;

  const roundedMinutes = Math.ceil(minutes / 15) * 15;

  if (roundedMinutes === 60) {
    startDate.setHours(startDate.getHours() + 1);
    startDate.setMinutes(0);
  } else {
    startDate.setMinutes(roundedMinutes);
  }
  startDate.setSeconds(0);
  startDate.setMilliseconds(0);
  return startDate;
};

/**
 * @param utcTimeDiff in format like "+05:00"
 */
export const getTimeZoneOffsetFromUTCOffsetInMinutes = (utcTimeDiff: string): number => {
  const regex = /([+-]?\d{1,2}):?(\d{2})?/;
  const match = regex.exec(utcTimeDiff);

  if (!match) {
    return 0;
  }
  const hours = parseInt(match[1], 10);
  const minutes = match[2] ? parseInt(match[2], 10) : 0;

  const sign = hours < 0 ? -1 : 1;
  return hours * 60 + sign * minutes;
};

/**
 * @param dateTime
 * @param utcTimeDiff In format: "+5:00"
 */
export const convertTimeFromTimezoneToUTC = (dateTime: Date, utcTimeDiff: string): Date => {
  const offsetInMilliseconds = getTimeZoneOffsetFromUTCOffsetInMinutes(utcTimeDiff) * 60 * 1000;
  const utcTime = dateTime.getTime() - offsetInMilliseconds;
  return new Date(utcTime);
};

export const convertUTCTimeToGivenTimezone = (dateTime: Date, utcTimeDiff: string): Date => {
  const offsetInMilliseconds = getTimeZoneOffsetFromUTCOffsetInMinutes(utcTimeDiff) * 60 * 1000;
  const utcTime = dateTime.getTime() + offsetInMilliseconds;
  return new Date(utcTime);
};

export const getTimeDifferenceFromNowInMilliseconds = (referenceDateTime: Date, referenceDateTimezoneOffset: number): number => {
  const currentDate = new Date();
  const currentTimezoneOffset = currentDate.getTimezoneOffset();
  const referenceTimezoneOffset = -referenceDateTimezoneOffset;

  const adjustedPublishDate = new Date(referenceDateTime.getTime() + (referenceTimezoneOffset - currentTimezoneOffset) * 60 * 1000);
  return adjustedPublishDate.getTime() - currentDate.getTime();
};

export const getTimeToOrFrom = (referenceDate: Date): string => {
  const differenceInSeconds = Math.floor((new Date().getTime() - referenceDate.getTime()) / NUM_MILLISECONDS_PER_SECOND);

  if (differenceInSeconds < 0) {
    return getTimeToDate(referenceDate);
  }

  return getTimeFromNow(referenceDate);
};

/**
 * checks the UTC/UNIX date against the current time and returns an internationalized string based on how much time has passed.
 * Examples: Just now, 1 minute ago, 1 hour ago, etc
 * @param {Date} oldDate
 * @return {string}
 */
export const getTimeFromNow = (oldDate: Date): string => {
  const {intervalInMinutes, intervalInHours, intervalInDays, intervalInWeeks, intervalInMonths, intervalInYears} = getTimeDifferenceFromNow(oldDate);

  if (intervalInYears === 1) {
    return window.i18next.t('pmwjs_1_year_ago');
  }
  if (intervalInYears > 1) {
    return window.i18next.t('pmwjs_x_years_ago', {years: intervalInYears});
  }

  if (intervalInMonths === 1) {
    return window.i18next.t('pmwjs_1_month_ago');
  }
  if (intervalInMonths > 1) {
    return window.i18next.t('pmwjs_x_months_ago', {months: intervalInMonths});
  }

  if (intervalInWeeks === 1) {
    return window.i18next.t('pmwjs_1_week_ago');
  }
  if (intervalInWeeks > 1) {
    return window.i18next.t('pmwjs_x_weeks_ago', {weeks: intervalInWeeks});
  }

  if (intervalInDays === 1) {
    return window.i18next.t('pmwjs_1_day_ago');
  }
  if (intervalInDays > 1) {
    return window.i18next.t('pmwjs_x_days_ago', {days: intervalInDays});
  }

  if (intervalInHours === 1) {
    return window.i18next.t('pmwjs_1_hour_ago');
  }
  if (intervalInHours > 1) {
    return window.i18next.t('pmwjs_x_hours_ago', {hours: intervalInHours});
  }

  if (intervalInMinutes === 1) {
    return window.i18next.t('pmwjs_1_minute_ago');
  }
  if (intervalInMinutes > 1) {
    return window.i18next.t('pmwjs_x_minutes_ago', {minutes: intervalInMinutes});
  }

  return window.i18next.t('pmwjs_just_now');
};

export const getTimeFromNowShort = (oldDate: Date): string => {
  const {intervalInMinutes, intervalInHours, intervalInDays, intervalInWeeks, intervalInMonths, intervalInYears} = getTimeDifferenceFromNow(oldDate);

  if (intervalInYears >= 1) {
    return window.i18next.t('pmwjs_x_years_short', {years: intervalInYears});
  }

  if (intervalInMonths >= 1) {
    return window.i18next.t('pmwjs_x_months_short', {months: intervalInMonths});
  }

  if (intervalInWeeks >= 1) {
    return window.i18next.t('pmwjs_x_weeks_short', {weeks: intervalInWeeks});
  }

  if (intervalInDays >= 1) {
    return window.i18next.t('pmwjs_x_days_short', {days: intervalInDays});
  }

  if (intervalInHours >= 1) {
    return window.i18next.t('pmwjs_x_hours_short', {hours: intervalInHours});
  }

  if (intervalInMinutes >= 1) {
    return window.i18next.t('pmwjs_x_minutes_short', {minutes: intervalInMinutes});
  }

  return window.i18next.t('pmwjs_just_now');
};

/**
 * How long until a future date, as "in a minute" / "in 40 minutes" / "in 2 hours" / "in 3 days".
 *
 * The operand order is load-bearing: getTimeDifferenceIntervalsBetweenTimes subtracts its second argument from its
 * first, so passing (now, futureDate) yields a NEGATIVE difference, every interval floors below 1, and the whole
 * ladder falls through to "just now" no matter how far away the date is. This read the other way around until
 * 2026-08-12 and so only ever returned "just now" — it had no callers to notice.
 */
export const getTimeToDate = (futureDate: Date): string => {
  return getTimeToDateStringFromIntervals(getTimeDifferenceIntervalsBetweenTimes(futureDate, new Date()));
};

export const getTimeToDateStringFromIntervals = (intervals: TimeIntervals): string => {
  const {intervalInMinutes, intervalInHours, intervalInDays, intervalInWeeks, intervalInMonths, intervalInYears} = intervals;

  if (intervalInYears === 1) {
    return window.i18next.t('pmwjs_in_a_year');
  }
  if (intervalInYears > 1) {
    return window.i18next.t('pmwjs_in_x_years', {years: intervalInYears});
  }

  if (intervalInMonths === 1) {
    return window.i18next.t('pmwjs_in_a_month');
  }
  if (intervalInMonths > 1) {
    return window.i18next.t('pmwjs_in_x_months', {months: intervalInMonths});
  }

  if (intervalInWeeks === 1) {
    return window.i18next.t('pmwjs_in_a_week');
  }
  if (intervalInWeeks > 1) {
    return window.i18next.t('pmwjs_in_x_weeks', {weeks: intervalInWeeks});
  }

  if (intervalInDays === 1) {
    return window.i18next.t('pmwjs_in_a_day');
  }
  if (intervalInDays > 1) {
    return window.i18next.t('pmwjs_in_x_days', {days: intervalInDays});
  }

  if (intervalInHours === 1) {
    return window.i18next.t('pmwjs_in_an_hour');
  }
  if (intervalInHours > 1) {
    return window.i18next.t('pmwjs_in_x_hours', {hours: intervalInHours});
  }

  if (intervalInMinutes === 1) {
    return window.i18next.t('pmwjs_in_a_minute');
  }
  if (intervalInMinutes > 1) {
    return window.i18next.t('pmwjs_in_x_minutes', {minutes: intervalInMinutes});
  }

  return window.i18next.t('pmwjs_just_now');
};

export const parseSecondsToMMSS = (
  time: undefined | number
): {
  minutes: string;
  seconds: string;
} => {
  if (!time) {
    return {
      minutes: '00',
      seconds: '00',
    };
  }
  let seconds = String(Math.round(time) % 60);

  if (seconds.length === 1) {
    seconds = `0${seconds}`;
  }

  let minutes = String(Math.floor(Math.round(time) / 60));
  if (minutes.length === 1) {
    minutes = `0${minutes}`;
  }

  return {
    minutes,
    seconds,
  };
};

export const convertSecondsToMMSSFormat = (time: undefined | number): string => {
  const {minutes, seconds} = parseSecondsToMMSS(time);

  return `${minutes}:${seconds}`;
};

export const convertSecondsToMinutesSecondsFormat = (time: undefined | number): string => {
  const {minutes, seconds} = parseSecondsToMMSS(time);

  return minutes === '0' ? `${seconds} sec` : `${minutes} min ${seconds} sec`;
};

export const formatTimeToDisplayFormat = (timeInSeconds: number): string => {
  if (timeInSeconds < 60) {
    return `${timeInSeconds.toFixed(1)}s`;
  }

  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = timeInSeconds % 60;

  return `${minutes}m ${seconds.toFixed(1)}s`;
};

export const getWeekdayMonthDateFormatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {weekday: 'long', day: 'numeric', month: 'short'}).format(date);
};

export const getSQLFormatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * sample: 2024-12-19
 * @param date
 */
export const getYearMonthDateFormat = (date: Date): string => {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();

  return `${year}-${month}-${day}`;
};


export const doDatesHaveSameMonth = (date1: Date, date2: Date): boolean => {
  return date1.getMonth() === date2.getMonth();
};

export const getTimeDifferenceIntervalsBetweenTimes = (dateTime1: Date, dateTime2: Date): TimeIntervals => {
  const diffInS = (dateTime1.getTime() - dateTime2.getTime()) / NUM_MILLISECONDS_PER_SECOND;
  return getTimeDifferenceIntervals(diffInS);
};

export const getTimeDifferenceIntervals = (differenceInSeconds: number): TimeIntervals => {
  const intervalInYears = Math.floor(differenceInSeconds / NUM_SECONDS_PER_YEAR);
  const intervalInMonths = Math.floor(differenceInSeconds / NUM_SECONDS_PER_MONTH);
  const intervalInWeeks = Math.floor(differenceInSeconds / NUM_SECONDS_PER_WEEK);
  const intervalInDays = Math.floor(differenceInSeconds / NUM_SECONDS_PER_DAY);
  const intervalInHours = Math.floor(differenceInSeconds / NUM_SECONDS_PER_HOUR);
  const intervalInMinutes = Math.floor(differenceInSeconds / NUM_SECONDS_PER_MINUTE);

  return {
    intervalInMinutes,
    intervalInHours,
    intervalInDays,
    intervalInWeeks,
    intervalInMonths,
    intervalInYears,
  };
};

export const getTimeDifferenceFromNow = (date: Date): TimeDifferenceFromNow => {
  const differenceInSeconds = Math.floor((new Date().getTime() - date.getTime()) / NUM_MILLISECONDS_PER_SECOND);

  return {
    differenceInSeconds,
    ...getTimeDifferenceIntervals(differenceInSeconds),
  };
};

export const getDurationString = (start: Date, end: Date): string => {
  const diffInMs = end.getTime() - start.getTime();
  const diffInSeconds = diffInMs / 1000;

  if (diffInSeconds < 60) {
    return `${Math.floor(diffInSeconds)} sec`;
  }

  const diffInMinutes = diffInSeconds / 60;
  if (diffInMinutes < 60) {
    return `${Math.floor(diffInMinutes)} mins`;
  }

  const diffInHours = diffInMinutes / 60;
  if (diffInHours < 24) {
    return `${Math.floor(diffInHours)} hours`;
  }

  const diffInDays = diffInHours / 24;
  return `${Math.floor(diffInDays)} days`;
};

/**
 * Given a timestamp, this function returns the time in the following format: HH:MM AM/PM
 * @param timestamp
 * @param opts
 */
export const formatTime = (timestamp: number, opts?: Intl.DateTimeFormatOptions): string => {
  const date = new Date(timestamp * 1000);

  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...opts,
  });

  return timeFormatter.format(date);
};

/**
 * Time + ordinal-date label (e.g. "3:45 PM, 14th June") — the time followed by the day-of-month with its
 * ordinal suffix and the month.
 */
export const getTimeAndDateLabel = (timestampInSeconds: number): string => {
  return `${formatTime(timestampInSeconds)}, ${getDateAndMonthStringWithOrdinal(new Date(timestampInSeconds * 1000))}`;
};

export const formatDateToShortWithTime = (dateStr: string): string => {
  const date = new Date(dateStr);

  return date
    .toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .replace(',', ' · ');
};

export function isTodayBetweenDates(lowerBound: Date, upperBound: Date): boolean {
  const today = new Date();

  let isBetween: boolean;

  if (lowerBound < upperBound) {
    isBetween = lowerBound <= today && today < upperBound;
  } else {
    isBetween = today >= lowerBound || today < upperBound;
  }

  return isBetween;
}
export const getStartOfDay = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

// example: 8 Sep - 15 Sep
export const formatDateRangeWithShortMonth = (startDate: Date, endDate: Date): string => {
  return `${getShortDateMonthFormat(startDate)} - ${getShortDateMonthFormat(endDate)}`;
};

// example: Sep 11, 10:00am - Sep 15, 2:00pm
export const formatDateRangeWithDateAndTime = (startDate: Date, endDate: Date): string => {
  return `${getMonthDateFormat(startDate)}, ${getReadableTimeForDate(startDate)} - ${getMonthDateFormat(endDate)}, ${getReadableTimeForDate(endDate)}`;
};
